import React, { useEffect, useState } from "react";
import {
  getTeams,
  getTeamPerformance,
} from "../services/teamService";

export default function TeamReportsPage() {
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] =
    useState("");

  const [performance, setPerformance] =
    useState(null);

  const [loadingTeams, setLoadingTeams] =
    useState(true);

  const [loadingReport, setLoadingReport] =
    useState(false);

  const [error, setError] = useState("");

  /* ===================================================== */
  /* LOAD TEAMS                                            */
  /* ===================================================== */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingTeams(true);
        setError("");

        const teamList = await getTeams();

        if (cancelled) return;

        setTeams(
          Array.isArray(teamList)
            ? teamList
            : []
        );

        if (
          Array.isArray(teamList) &&
          teamList.length > 0
        ) {
          setSelectedTeamId(
            teamList[0]._id
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load teams"
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingTeams(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ===================================================== */
  /* LOAD TEAM REPORT                                      */
  /* ===================================================== */

  useEffect(() => {
    if (!selectedTeamId) return;

    let cancelled = false;

    (async () => {
      try {
        setLoadingReport(true);
        setError("");

        const report =
          await getTeamPerformance(
            selectedTeamId
          );

        if (cancelled) return;

        setPerformance(report);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load report"
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingReport(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedTeamId]);

  /* ===================================================== */
  /* METRIC CARD                                           */
  /* ===================================================== */

  const MetricCard = ({
    title,
    value,
  }) => {
    return (
      <div className="rounded-xl border bg-white p-5">
        <div className="text-sm text-gray-500">
          {title}
        </div>

        <div className="mt-2 text-3xl font-semibold text-gray-900">
          {value}
        </div>
      </div>
    );
  };

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  return (
    <div className="max-w-7xl mx-auto p-6">

      {/* HEADER */}

      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-gray-900">
          Team Reports
        </h1>

        <p className="text-gray-500 mt-1">
          Track team productivity,
          task completion,
          deadlines and member
          performance.
        </p>
      </div>

      {/* TEAM SELECTOR */}

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Team
        </label>

        <select
          value={selectedTeamId}
          onChange={(e) =>
            setSelectedTeamId(
              e.target.value
            )
          }
          className="
            w-full
            max-w-md
            rounded-xl
            border
            border-gray-300
            px-4
            py-3
          "
        >
          <option value="">
            Select Team
          </option>

          {teams.map((team) => (
            <option
              key={team._id}
              value={team._id}
            >
              {team.name}
            </option>
          ))}
        </select>
      </div>

      {/* ERROR */}

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-600">
          {error}
        </div>
      )}

      {/* LOADING */}

      {(loadingTeams ||
        loadingReport) && (
        <div className="rounded-xl border bg-white p-6">
          Loading report...
        </div>
      )}

      {/* REPORT */}

      {!loadingReport &&
        performance && (
          <>
            {/* TEAM STATS */}

            <div className="grid gap-4 md:grid-cols-4 mb-8">

              <MetricCard
                title="Members"
                value={
                  performance.members
                }
              />

              <MetricCard
                title="Assigned Tasks"
                value={
                  performance.assignedTasks
                }
              />

              <MetricCard
                title="Completed Tasks"
                value={
                  performance.completedTasks
                }
              />

              <MetricCard
                title="Active Tasks"
                value={
                  performance.activeTasks
                }
              />

              <MetricCard
                title="Overdue Tasks"
                value={
                  performance.overdueTasks
                }
              />

              <MetricCard
                title="Completed On Time"
                value={
                  performance.completedOnTime
                }
              />

              <MetricCard
                title="Completed Late"
                value={
                  performance.completedLate
                }
              />

              <MetricCard
                title="Completion Rate"
                value={`${performance.completionRate}%`}
              />

            </div>

            {/* MEMBER BREAKDOWN */}

            <div className="rounded-xl border bg-white overflow-hidden">

              <div className="border-b px-6 py-4">
                <h2 className="text-lg font-semibold">
                  Member Breakdown
                </h2>
              </div>

              <div className="overflow-x-auto">

                <table className="w-full">

                  <thead className="bg-gray-50">

                    <tr>

                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                        Member
                      </th>

                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                        Assigned
                      </th>

                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                        Completed
                      </th>

                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">
                        Overdue
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {performance.memberBreakdown?.map(
                      (member) => (
                        <tr
                          key={
                            member.userId
                          }
                          className="border-t"
                        >
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">
                              {member.name}
                            </div>

                            <div className="text-sm text-gray-500">
                              {member.email}
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            {
                              member.assignedTasks
                            }
                          </td>

                          <td className="px-6 py-4">
                            {
                              member.completedTasks
                            }
                          </td>

                          <td className="px-6 py-4">
                            {
                              member.overdueTasks
                            }
                          </td>
                        </tr>
                      )
                    )}

                    {(!performance.memberBreakdown ||
                      performance.memberBreakdown
                        .length === 0) && (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          No team members found
                        </td>
                      </tr>
                    )}

                  </tbody>

                </table>

              </div>

            </div>
          </>
        )}
    </div>
  );
}